import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { buildFontFamilyCssVar, buildFontFamilyStyle, parseCssStringToObject } from "../../../helper";
import { DEFAULT_BLOCK_CSS } from "../../../constant/constant";
import {
  getTrustBadgesDisplay,
  getTrustIconEmoji,
  resolveTrustBadgeItems,
  TRUST_BADGES_STYLE,
  TRUST_BADGES_ACCENT,
} from "../../../../shared/product-block-display.js";
import styles from "./AiTrustSignalsRow.module.css";

function TrustBadgeItem({ item, display, layout }) {
  const emoji = display.showIcons !== false ? getTrustIconEmoji(item.icon) : null;
  const label = item.label;

  if (layout === TRUST_BADGES_STYLE.CARD_TRIO) {
    return (
      <div className={styles.card}>
        {emoji ? (
          <span className={styles.cardIcon} aria-hidden>
            {emoji}
          </span>
        ) : null}
        <span className={styles.cardLabel}>{label}</span>
      </div>
    );
  }

  if (layout === TRUST_BADGES_STYLE.ICON_GRID) {
    return (
      <div className={styles.gridItem}>
        {emoji ? (
          <span className={styles.gridIcon} aria-hidden>
            {emoji}
          </span>
        ) : null}
        <span className={styles.gridLabel}>{label}</span>
      </div>
    );
  }

  if (layout === TRUST_BADGES_STYLE.PILL_STRIP) {
    return (
      <span className={styles.pill}>
        {emoji ? (
          <span className={styles.pillIcon} aria-hidden>
            {emoji}
          </span>
        ) : null}
        <span className={styles.pillLabel}>{label}</span>
      </span>
    );
  }

  return (
    <span className={styles.item}>
      {emoji ? (
        <span className={styles.icon} aria-hidden>
          {emoji}
        </span>
      ) : null}
      {label}
    </span>
  );
}

export default function AiTrustSignalsRow({
  text,
  section,
  isMobile = false,
  flexWrap = false,
}) {
  const viewKey = isMobile ? "mobile" : "desktop";
  const blockCSS = useMemo(
    () => ({
      ...DEFAULT_BLOCK_CSS,
      ...section?.setting?.customCSS?.[viewKey],
      ...parseCssStringToObject(section?.setting?.overrideCss?.[viewKey]),
    }),
    [section, viewKey]
  );

  const hasCustomTypography = Boolean(
    blockCSS.fontFamily || blockCSS.fontSize || blockCSS.fontWeight
  );

  const rootStyle = useMemo(() => {
    const {
      fontFamily,
      backgroundColor,
      borderRadius,
      borderWidth,
      borderStyle,
      borderColor,
      // Keep chrome off the outer row — Design bg/border/radius target each badge.
      padding: _padding,
      margin: _margin,
      width: _width,
      height: _height,
      ...restCss
    } = blockCSS;

    const hasMerchantBg =
      backgroundColor != null &&
      backgroundColor !== "" &&
      backgroundColor !== "transparent";

    const radiusValue =
      borderRadius == null || borderRadius === ""
        ? null
        : typeof borderRadius === "number"
          ? `${borderRadius}px`
          : String(borderRadius);

    const bw = Number(borderWidth);
    const hasBorderWidth = borderWidth != null && borderWidth !== "" && Number.isFinite(bw);
    const badgeBorder = hasBorderWidth
      ? bw > 0
        ? `${bw}px ${borderStyle || "solid"} ${borderColor || "#e2e8f0"}`
        : "0 solid transparent"
      : null;

    return {
      ...restCss,
      backgroundColor: "transparent",
      ...buildFontFamilyCssVar("--trust-font-family", fontFamily),
      ...buildFontFamilyStyle(fontFamily),
      ...(blockCSS.fontSize ? { "--trust-font-size": blockCSS.fontSize } : {}),
      ...(blockCSS.fontWeight
        ? { "--trust-font-weight": blockCSS.fontWeight }
        : {}),
      ...(blockCSS.color ? { "--trust-color": blockCSS.color } : {}),
      ...(hasMerchantBg
        ? {
            "--trust-badge-bg": backgroundColor,
            "--trust-badge-padding": "6px 12px",
          }
        : {}),
      ...(radiusValue ? { "--trust-badge-radius": radiusValue } : {}),
      ...(badgeBorder ? { "--trust-badge-border": badgeBorder } : {}),
    };
  }, [blockCSS]);

  const display = section ? getTrustBadgesDisplay(section) : {};
  const items = useMemo(
    () => (section ? resolveTrustBadgeItems(section) : []),
    [section, text]
  );

  const fallbackText = String(text || section?.text || "").trim();
  const layout = display.style || TRUST_BADGES_STYLE.PILL_STRIP;
  const accent = display.accent || TRUST_BADGES_ACCENT.NEUTRAL;

  const mobileClass =
    isMobile && !flexWrap && !hasCustomTypography ? styles.mobileCompact : "";
  const wrapClass = flexWrap ? styles.flexWrapFriendly : "";
  const rootClassName = [
    styles.root,
    hasCustomTypography ? styles.customTypography : "",
    mobileClass,
    wrapClass,
  ]
    .filter(Boolean)
    .join(" ");

  if (layout === TRUST_BADGES_STYLE.TEXT_ONLY) {
    if (!fallbackText) return null;
    return (
      <div
        className={`${rootClassName} ${styles.accentNeutral}`.trim()}
        style={rootStyle}
        data-has-custom-typography={hasCustomTypography || undefined}
      >
        <p className={styles.textOnly}>{fallbackText}</p>
      </div>
    );
  }

  if (!items.length && !fallbackText) return null;

  const accentClass =
    accent === TRUST_BADGES_ACCENT.MINT
      ? styles.accentMint
      : accent === TRUST_BADGES_ACCENT.SLATE
        ? styles.accentSlate
        : styles.accentNeutral;

  const layoutClass =
    layout === TRUST_BADGES_STYLE.CARD_TRIO
      ? styles.layoutCards
      : layout === TRUST_BADGES_STYLE.ICON_GRID
        ? styles.layoutGrid
        : layout === TRUST_BADGES_STYLE.PILL_STRIP
          ? styles.layoutPills
          : styles.layoutInline;

  if (!items.length) {
    return (
      <div
        className={`${rootClassName} ${accentClass} ${styles.layoutInline}`.trim()}
        style={rootStyle}
        data-has-custom-typography={hasCustomTypography || undefined}
      >
        <span className={styles.textOnly}>{fallbackText}</span>
      </div>
    );
  }

  return (
    <div
      className={`${rootClassName} ${accentClass} ${layoutClass}`.trim()}
      style={rootStyle}
      data-trust-layout={layout}
      data-has-custom-typography={hasCustomTypography || undefined}
    >
      {items.map((item) => (
        <TrustBadgeItem key={item.id || item.label} item={item} display={display} layout={layout} />
      ))}
    </div>
  );
}

AiTrustSignalsRow.propTypes = {
  text: PropTypes.string,
  section: PropTypes.object,
  isMobile: PropTypes.bool,
  flexWrap: PropTypes.bool,
};
